import { createFileRoute, Outlet, redirect } from '@tanstack/react-router'
import { Box, Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { NavTab, NavTabs } from '@shared/components'
import { useEnablements } from '@portal/pkg-authorization'
import type { ReactElement } from 'react'
import type { NavTabProps } from '@shared/components/NavTabs/NavTab'

export const Route = createFileRoute('/_auth/preferences/navigation-bar')({
  component: RouteComponent,
  beforeLoad: ({ context: { scopedEnablements }, location }) => {
    const canMenu = scopedEnablements.global().hasStandardEnablement('Menu', 'WRITE')
    const canLogo = scopedEnablements.global().hasStandardEnablement('Organization', 'MANAGE')

    if (!canMenu && !canLogo) {
      throw redirect({
        to: '/403',
        replace: true,
      })
    }

    // Default index is Menu Designer — if user only has logo entitlement, send them there.
    const path = location.pathname.replace(/\/$/, '')
    const isIndex =
      path === '/preferences/navigation-bar' || path.endsWith('/preferences/navigation-bar')
    if (isIndex && !canMenu && canLogo) {
      throw redirect({
        to: '/preferences/navigation-bar/org-logo',
        replace: true,
      })
    }
  },
})

function RouteComponent() {
  const { t } = useTranslation('admin')
  const enablements = useEnablements({ global: true })

  const canMenu = !!enablements?.hasStandardEnablement('Menu', 'WRITE')
  const canLogo = !!enablements?.hasStandardEnablement('Organization', 'MANAGE')

  return (
    <Box sx={{ width: '100%', pl: 2 }}>
      <Stack
        direction="row"
        sx={{
          justifyContent: 'space-between',
          mb: 1,
        }}
      >
        <Typography variant="h5">{t('preferences.navigationBar.title')}</Typography>
      </Stack>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mr: 2 }}>
        <NavTabs>
          {
            [
              canMenu ? (
                <NavTab
                  key="menuDesigner"
                  value="menuDesigner"
                  to="/preferences/navigation-bar"
                  activeOptions={{ exact: true }}
                  label={t('preferences.navigationBar.tabs.menuDesigner')}
                />
              ) : null,
              canLogo ? (
                <NavTab
                  key="orgLogo"
                  value="orgLogo"
                  to="/preferences/navigation-bar/org-logo"
                  label={t('preferences.navigationBar.tabs.organizationLogo')}
                />
              ) : null,
            ].filter(Boolean) as ReactElement<NavTabProps>[]
          }
        </NavTabs>
      </Box>
      <Box sx={{ p: 2, height: 'calc(100vh - 135px)', overflow: 'auto' }}>
        <Outlet />
      </Box>
    </Box>
  )
}
